import type { CSSProperties } from 'react';
import { getFontFamily } from '@/theme/typography';
import {
  AVATAR_ICON_SIZE,
  AVATAR_PAD_Y,
  AVATAR_WIDTH,
  CARD_PAD_X,
  CARD_PAD_Y,
  CARD_RADIUS,
  GLASS_SHADOW,
  MESSAGE_GAP,
  MESSAGE_LINE_HEIGHT,
  MESSAGE_TEXT_GRADIENT,
  MINT,
  PAGE_BACKGROUND,
  ROW_GAP,
  TITLE_INDENT,
} from './chatteaser3.constants';

export const CSS_APPEARANCE_NONE: CSSProperties['appearance'] = 'none';

export const PAGE_STYLE: CSSProperties = {
  height: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: PAGE_BACKGROUND,
  fontFamily: getFontFamily(),
  touchAction: 'pan-y',
};

export const WRAPPER_STYLE: CSSProperties = {
  position: 'absolute',
  left: '8.1%',
  top: '8.1%',
  bottom: '8.1%',
  width: '51.9%',
  overflow: 'hidden',
  // Clip both cards to the rounded container shape, so the collapsing UNITED card
  // can never poke out past the rounded bottom edge.
  borderRadius: CARD_RADIUS,
};

export const SOLO_CARD_STYLE: CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1,
  boxSizing: 'border-box',
  overflow: 'hidden',
  padding: `${CARD_PAD_Y}px var(--ct3-pad-x, ${CARD_PAD_X}px)`,
  borderRadius: CARD_RADIUS,
  backgroundColor: 'rgba(255,255,255,0.07)',
  boxShadow: GLASS_SHADOW,
  display: 'flex',
  flexDirection: 'column',
};

export const SOLO_CONTENT_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

const UNITED_CARD_BG = [
  'radial-gradient(circle at 110% 100%, rgba(95,243,184,0.15) 0%, rgba(95,243,184,0.06) 31%, rgba(95,243,184,0.01) 61%, rgba(0,0,0,0.00) 80%)',
  'radial-gradient(circle at 5.3% 4.6%, rgba(95,243,184,0.15) 0%, rgba(95,243,184,0.06) 36%, rgba(95,243,184,0.01) 69%, rgba(0,0,0,0.00) 90%)',
].join(', ');

export const UNITED_CARD_STYLE: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2,
  boxSizing: 'border-box',
  // Only the top corners are rounded here; the bottom is clipped by the wrapper's
  // rounded corners, so the collapsing frame fills the rounded bottom cleanly.
  borderTopLeftRadius: CARD_RADIUS,
  borderTopRightRadius: CARD_RADIUS,
  backgroundColor: 'rgba(0,0,0,0.72)',
  backgroundImage: UNITED_CARD_BG,
  boxShadow: GLASS_SHADOW,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

export const UNITED_HEADER_STYLE: CSSProperties = {
  flexShrink: 0,
  padding: `${CARD_PAD_Y}px var(--ct3-pad-x, ${CARD_PAD_X}px) 60px`,
};

export const UNITED_SCROLLER_STYLE: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  boxSizing: 'border-box',
  marginTop: -80,
  padding: `100px var(--ct3-pad-x, ${CARD_PAD_X}px) 0`,
  maskImage: 'linear-gradient(transparent 0px, rgb(0, 0, 0) 32px)',
  WebkitMaskImage: 'linear-gradient(transparent 0px, rgb(0, 0, 0) 32px)',
};

export const MESSAGES_INNER_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: MESSAGE_GAP,
  paddingBottom: CARD_PAD_Y,
};

export const SECTION_HEADER_STYLE: CSSProperties = {
  display: 'flex',
  gap: '0.7ch',
  marginLeft: `var(--ct3-title-indent, ${TITLE_INDENT}px)`,
  padding: '20px 0 26px',
  fontSize: 26,
  fontWeight: 700,
  letterSpacing: '0.34em',
  textTransform: 'uppercase',
};

export const HEADER_MINT_STYLE: CSSProperties = { color: MINT };
export const HEADER_MUTED_STYLE: CSSProperties = {
  color: 'rgba(255, 255, 255, 0.66)',
};

export const MESSAGE_ROW_STYLE: CSSProperties = {
  position: 'relative',
  display: 'flex',
  gap: `var(--ct3-row-gap, ${ROW_GAP}px)`,
  alignItems: 'stretch',
};

export const AVATAR_SLOT_STYLE: CSSProperties = {
  position: 'relative',
  width: `var(--ct3-avatar-w, ${AVATAR_WIDTH}px)`,
  flexShrink: 0,
};

export const AVATAR_CAPSULE_BASE_STYLE: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: `var(--ct3-avatar-w, ${AVATAR_WIDTH}px)`,
  boxSizing: 'border-box',
  borderRadius: CARD_RADIUS,
  backgroundColor: 'rgba(255,255,255,0.07)',
  boxShadow: GLASS_SHADOW,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `var(--ct3-avatar-pad-y, ${AVATAR_PAD_Y}px) 0`,
  willChange: 'height, transform',
};

export const AVATAR_ICON_STYLE: CSSProperties = {
  width: `var(--ct3-avatar-icon, ${AVATAR_ICON_SIZE}px)`,
  height: `var(--ct3-avatar-icon, ${AVATAR_ICON_SIZE}px)`,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const AVATAR_NAME_STYLE: CSSProperties = {
  fontSize: 13,
  color: 'rgba(255, 255, 255, 0.5)',
  whiteSpace: 'nowrap',
};

export const MESSAGE_CONTENT_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  minWidth: 0,
  flex: 1,
};

export const ROLE_TAG_WRAP_STYLE: CSSProperties = {
  position: 'relative',
  alignSelf: 'flex-start',
  minWidth: 'var(--ct3-role-minw, 300px)',
  borderRadius: CARD_RADIUS,
  overflow: 'hidden',
  // Tuck the tag left so it sits behind the avatar; the inner's left padding
  // keeps the title clear of it.
  transform: 'translateX(calc(-1 * var(--ct3-role-shift, 80px)))',
};

export const ROLE_TAG_BG_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  opacity: 0.18,
  borderRadius: CARD_RADIUS,
  backgroundImage: 'linear-gradient(90deg, rgba(95,243,184,0.00) 23%, rgba(95,243,184,0.27) 94%)',
  boxShadow: GLASS_SHADOW,
};

export const ROLE_TAG_INNER_STYLE: CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '11px 26px 11px var(--ct3-role-pad-l, 100px)',
};

export const ROLE_BRACKET_STYLE: CSSProperties = {
  color: 'rgba(255, 255, 255, 0.45)',
  fontSize: 24,
};

export const ROLE_TEXT_STYLE: CSSProperties = {
  flex: 1,
  textAlign: 'center',
  color: MINT,
  fontSize: 26,
  fontWeight: 500,
};

export const MESSAGE_TEXT_STYLE: CSSProperties = {
  margin: 0,
  fontSize: 28,
  lineHeight: MESSAGE_LINE_HEIGHT,
  backgroundImage: MESSAGE_TEXT_GRADIENT,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  WebkitTextFillColor: 'transparent',
  whiteSpace: 'pre-line',
};

export const MENTION_CHIP_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  margin: '0 2px',
  padding: '3px 11px 3px 4px',
  borderRadius: CARD_RADIUS,
  backgroundColor: 'rgba(95,243,184,0.10)',
  border: '1px solid rgba(95,243,184,0.28)',
  color: MINT,
  WebkitTextFillColor: MINT,
  fontSize: 19,
  fontWeight: 500,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

export const MENTION_ICON_STYLE: CSSProperties = {
  width: 24,
  height: 24,
  flexShrink: 0,
  borderRadius: '50%',
  overflow: 'hidden',
  backgroundColor: 'rgba(255,255,255,0.08)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};
